import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Textarea } from '@/components/atoms/Textarea';

interface Report {
  id: number;
  target_type: 'post' | 'comment' | 'user';
  target_id: number;
  reporter_id: string | null;
  reporter_nickname: string | null;
  reason: string | null;
  reason_category: string | null;
  reason_memo: string | null;
  description: string | null;
  priority: 'auto_high' | 'normal';
  status: string;
  matched_keyword: string | null;
  resolved_action: string | null;
  resolved_memo: string | null;
  processed_at: string | null;
  created_at: string;
  report_count: number;
  target_preview:
    | {
        id: number;
        title?: string;
        post_id?: number;
        nickname: string | null;
        preview: string;
        is_blinded: boolean;
        is_deleted: boolean;
      }
    | null;
}

const ACTIONS = [
  { value: 'post_hidden', label: '게시글 숨김' },
  { value: 'post_deleted', label: '게시글 삭제' },
  { value: 'comment_hidden', label: '댓글 숨김' },
  { value: 'comment_deleted', label: '댓글 삭제' },
  { value: 'no_action', label: '조치없음 (resolved)' },
  { value: 'dismissed', label: '기각' },
];

function fmt(iso: string | null) {
  if (!iso) return '-';
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
}

export function ReportsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'pending';
  const page = Number(params.get('page') ?? 1);

  const [selected, setSelected] = useState<Report | null>(null);
  const [action, setAction] = useState<string>('post_hidden');
  const [memo, setMemo] = useState('');

  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'reports', { status, page }],
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('status', status);
      sp.set('page', String(page));
      sp.set('limit', '30');
      return get<{ reports: Report[]; total: number }>(`/admin/reports?${sp}`);
    },
  });

  const resolveMut = useMutation({
    mutationFn: (reportId: number) =>
      post(`/admin/reports/${reportId}/resolve`, { action, memo }),
    onSuccess: () => {
      setSelected(null);
      setMemo('');
      qc.invalidateQueries({ queryKey: ['admin', 'reports'] });
    },
  });

  return (
    <div className="w-full">
      <h1 className="mb-6 text-2xl font-extrabold">신고 처리</h1>

      <div className="mb-4 flex gap-2">
        {(['pending', 'processed', 'dismissed'] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={status === s ? 'primary' : 'ghost'}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('status', s);
              next.set('page', '1');
              setParams(next);
            }}
          >
            {s === 'pending' ? '대기' : s === 'processed' ? '처리완료' : '기각'}
          </Button>
        ))}
        <div className="ml-auto text-xs text-[var(--color-text-sub)]">총 {data?.total ?? 0}건</div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[420px_1fr]">
        {/* 좌측: 신고 리스트 */}
        <div className="max-h-[80vh] overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          {isLoading ? (
            <Skeleton className="h-96" />
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {data?.reports.map((r) => {
                const active = selected?.id === r.id;
                return (
                  <li
                    key={r.id}
                    onClick={() => {
                      setSelected(r);
                      setAction(
                        r.target_type === 'post'
                          ? 'post_hidden'
                          : r.target_type === 'comment'
                            ? 'comment_hidden'
                            : 'no_action',
                      );
                      setMemo('');
                    }}
                    className={`cursor-pointer p-3 hover:bg-[var(--color-bg)] ${
                      active ? 'bg-[var(--color-bg)]' : ''
                    }`}
                  >
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      {r.priority === 'auto_high' && <Badge variant="fall">자동신고</Badge>}
                      <Badge variant="outline">{r.target_type}</Badge>
                      {r.report_count > 1 && (
                        <Badge variant="fall">{r.report_count}건</Badge>
                      )}
                      <span className="ml-auto text-xs text-[var(--color-text-sub)]">
                        {fmt(r.created_at)}
                      </span>
                    </div>
                    <p className="text-sm font-medium">
                      {r.reason_category ?? r.reason ?? '미분류'}
                    </p>
                    {r.target_preview && (
                      <p className="mt-1 line-clamp-2 text-xs text-[var(--color-text-sub)]">
                        {r.target_preview.title ?? r.target_preview.preview}
                      </p>
                    )}
                    {r.matched_keyword && (
                      <p className="mt-1 text-xs text-[var(--color-warn)]">
                        매칭: {r.matched_keyword}
                      </p>
                    )}
                  </li>
                );
              })}
              {data?.reports.length === 0 && (
                <li className="p-8 text-center text-[var(--color-text-sub)]">
                  처리할 신고가 없어요.
                </li>
              )}
            </ul>
          )}
        </div>

        {/* 우측: 상세 + 액션 */}
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
          {!selected ? (
            <p className="text-center text-sm text-[var(--color-text-sub)]">
              좌측에서 신고를 선택하세요.
            </p>
          ) : (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold">
                  신고 #{selected.id} ·{' '}
                  <span className="font-normal text-[var(--color-text-sub)]">
                    {selected.target_type} #{selected.target_id}
                  </span>
                </h2>
                <p className="mt-1 text-xs text-[var(--color-text-sub)]">
                  {fmt(selected.created_at)} · 신고자{' '}
                  {selected.reporter_nickname ?? (selected.priority === 'auto_high' ? '자동' : '익명')}
                </p>
              </div>

              <section className="space-y-1 text-sm">
                <div>
                  <span className="text-[var(--color-text-sub)]">사유 카테고리:</span>{' '}
                  <strong>{selected.reason_category ?? selected.reason ?? '-'}</strong>
                </div>
                {(selected.reason_memo || selected.description) && (
                  <div>
                    <span className="text-[var(--color-text-sub)]">메모:</span>{' '}
                    {selected.reason_memo ?? selected.description}
                  </div>
                )}
                {selected.matched_keyword && (
                  <div>
                    <span className="text-[var(--color-text-sub)]">매칭 키워드:</span>{' '}
                    <code>{selected.matched_keyword}</code>
                  </div>
                )}
              </section>

              {selected.target_preview && (
                <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-sm">
                  <div className="mb-1 text-xs text-[var(--color-text-sub)]">대상 콘텐츠</div>
                  {selected.target_preview.title && (
                    <p className="font-semibold">{selected.target_preview.title}</p>
                  )}
                  <p className="mt-1 whitespace-pre-wrap">{selected.target_preview.preview}</p>
                  <div className="mt-2 flex gap-2 text-xs text-[var(--color-text-sub)]">
                    <span>by {selected.target_preview.nickname ?? '-'}</span>
                    {selected.target_preview.is_blinded && <Badge variant="fall">블라인드</Badge>}
                    {selected.target_preview.is_deleted && <Badge variant="default">삭제</Badge>}
                    {selected.target_type === 'post' && (
                      <Link
                        to={`/post/${selected.target_id}`}
                        className="ml-auto text-[var(--color-primary)] hover:underline"
                      >
                        원본 ↗
                      </Link>
                    )}
                  </div>
                </section>
              )}

              {selected.status === 'pending' ? (
                <section>
                  <h3 className="mb-2 text-sm font-semibold">처리</h3>
                  <select
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    className="h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm"
                  >
                    {ACTIONS.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                  <Textarea
                    rows={2}
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    placeholder="처리 메모 (선택)"
                    className="mt-2 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm"
                  />
                  <Button
                    fullWidth
                    className="mt-3"
                    loading={resolveMut.isPending}
                    onClick={() => resolveMut.mutate(selected.id)}
                  >
                    처리 적용
                  </Button>
                </section>
              ) : (
                <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-sm">
                  <div>
                    상태: <Badge>{selected.status}</Badge>
                  </div>
                  {selected.resolved_action && (
                    <div className="mt-1">처리 액션: {selected.resolved_action}</div>
                  )}
                  {selected.resolved_memo && <div className="mt-1">메모: {selected.resolved_memo}</div>}
                  {selected.processed_at && (
                    <div className="mt-1 text-xs text-[var(--color-text-sub)]">
                      {fmt(selected.processed_at)}
                    </div>
                  )}
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
